import React, { useMemo } from 'react';
import { defaultMusicVideoDoc, parseMusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoEditorResponse, MusicVideoSongMap } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../common/types';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { LoadingView } from '../common/views/LoadingView';
import { useRealtimePollInterval } from '../realtime/useRealtimePollInterval';
import { EDITOR_POLL_MS } from './editorPolling';
import { MusicVideoEditor } from './MusicVideoEditor';
import { useGetFootageQuery, useGetMusicVideoCutQuery } from './musicVideoEditorApi';
import { DirectorProgress } from './states/DirectorProgress';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from './MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MusicVideoCutLoaderProps {
  accountId: number;
  catalogItemUuid: string;
  cutUuid: string;
  data: MusicVideoEditorResponse;
  songMap: MusicVideoSongMap;
  readOnly: boolean;
  onLeave: () => void;
  onOpenCut: (cutUuid: string) => void;
}

/**
 * Reads one cut (`GET .../cuts/:cutUuid`). While the director writes a cut with no clips yet, it
 * shows the director card (design e1); else it opens the editor on the cut. A document that does
 * not parse (a newer shape) opens as a new empty cut of the song.
 */
const MusicVideoCutLoaderComponent: React.FC<MusicVideoCutLoaderProps> = (props) => {
  const { accountId, catalogItemUuid, cutUuid, data, songMap, readOnly, onLeave, onOpenCut } = props;
  const summary = data.cuts.find((cut) => cut.uuid === cutUuid);
  const isWorking = summary?.directorState === 'working';
  const pollingInterval = useRealtimePollInterval(isWorking ? EDITOR_POLL_MS : 0);
  const cutQuery = useGetMusicVideoCutQuery({ accountId, catalogItemUuid, cutUuid }, { pollingInterval });
  const footage = useGetFootageQuery({ accountId, catalogItemUuid });
  const ownClipCount = useMemo(
    () =>
      (footage.data?.clips ?? []).filter((clip) => clip.catalogItemUuid === catalogItemUuid && clip.status === 'ready')
        .length,
    [catalogItemUuid, footage.data]
  );
  const cut = cutQuery.data?.cut;
  const durationSec = data.song.durationSec ?? 0;
  const initialDoc = useMemo(() => {
    if (cut === undefined) {
      return null;
    }
    const parsed = parseMusicVideoDoc(cut.doc, { durationSec });
    return parsed.ok ? parsed.doc : defaultMusicVideoDoc(durationSec);
  }, [cut, durationSec]);

  if (cutQuery.error !== undefined && cut === undefined) {
    return (
      <div className={styles.fullState}>
        <ErrorView error={cutQuery.error} retry={() => cutQuery.refetch().catch(console.error)} />
      </div>
    );
  }
  if (cut === undefined || initialDoc === null) {
    return (
      <div className={styles.fullState}>
        <LoadingView />
      </div>
    );
  }
  if (cut.directorState === 'working' && initialDoc.clips.length === 0) {
    return (
      <div className={styles.fullState}>
        <DirectorProgress title={data.song.title} songMap={songMap} ownClipCount={ownClipCount} />
      </div>
    );
  }
  return (
    <MusicVideoEditor
      accountId={accountId}
      catalogItemUuid={catalogItemUuid}
      cut={cut}
      initialDoc={initialDoc}
      data={data}
      songMap={songMap}
      readOnly={readOnly}
      onLeave={onLeave}
      onOpenCut={onOpenCut}
    />
  );
};

export const MusicVideoCutLoader = TypedMemo(MusicVideoCutLoaderComponent);
